<div class="user-form-container">
  <!-- Loading Spinner -->
  @if (saving || loadingUserData) {
    <div class="loading-overlay">
      <mat-spinner diameter="60"></mat-spinner>
      <p>{{ saving ? 'Saving user...' : 'Loading user data...' }}</p>
    </div>
  }

  <div class="form-content">

    <!-- Header Section -->
    <div class="header-section">
      <div class="title-section">
        <h1>{{ isEditMode ? 'Edit User' : 'New User' }}</h1>
        @if (isEditMode && currentUser?.role) {
          <div class="role-display">
            <mat-icon>admin_panel_settings</mat-icon>
            <span class="role-text">{{ currentUser.role }}</span>
          </div>
        }
        <p class="subtitle">{{ isEditMode ? 'Update user details' : 'Create a new user record' }}</p>
        <p class="required-fields-note">Fields marked with <span class="required-indicator">*</span> are required</p>
      </div>

      <div class="header-actions">
        <button mat-stroked-button color="accent" (click)="cancelSubmit()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!isFormValid() || saving"
                (click)="onSubmit()">
          <mat-icon>save</mat-icon>
          {{ isEditMode ? 'Update User' : 'Save User' }}
        </button>
      </div>
    </div>

    <form [formGroup]="form" (ngSubmit)="onSubmit()">

      <!-- Name Section -->
      <mat-card class="hero-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>person</mat-icon>
            Personal Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          @if (isEditMode) {
            <!-- Read-only view for edit mode - names come from Cognito -->
            <div class="readonly-info-section">
              <p class="info-note">
                <mat-icon>info</mat-icon>
                User names are managed by your SSO/Cognito identity provider and cannot be edited here.
              </p>
              <div class="form-grid three-column">
                <mat-form-field appearance="outline">
                  <mat-label>First Name</mat-label>
                  <input matInput
                         [value]="currentUser?.given_name || currentUser?.firstName || 'N/A'"
                         readonly>
                  <mat-icon matSuffix>lock</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Last Name</mat-label>
                  <input matInput
                         [value]="currentUser?.family_name || currentUser?.lastName || 'N/A'"
                         readonly>
                  <mat-icon matSuffix>lock</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Friendly Name</mat-label>
                  <input matInput
                         formControlName="friendlyName"
                         readonly>
                  <mat-icon matSuffix>lock</mat-icon>
                </mat-form-field>
              </div>
            </div>
          } @else {
            <!-- Editable fields for new user mode -->
            <div class="form-grid three-column">
              <mat-form-field appearance="outline">
                <mat-label>First Name <span class="required-indicator">*</span></mat-label>
                <input matInput
                       formControlName="firstName"
                       placeholder="Enter first name"
                       required>
                <mat-icon matSuffix>person</mat-icon>
                @if (form.get('firstName')?.hasError('required')) {
                  <mat-error>First name is required</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Middle Name</mat-label>
                <input matInput
                       formControlName="middleName"
                       placeholder="Enter middle name">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Name <span class="required-indicator">*</span></mat-label>
                <input matInput
                       formControlName="lastName"
                       placeholder="Enter last name"
                       required>
                <mat-icon matSuffix>person</mat-icon>
                @if (form.get('lastName')?.hasError('required')) {
                  <mat-error>Last name is required</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Suffix</mat-label>
                <input matInput
                       formControlName="suffix"
                       placeholder="Jr., Sr., III, etc.">
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Full Name (Auto-generated)</mat-label>
                <input matInput
                       formControlName="userFullName"
                       placeholder="Auto-generated from first/middle/last"
                       readonly>
                <mat-icon matSuffix>auto_fix_high</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Friendly Name</mat-label>
                <input matInput
                       formControlName="friendlyName"
                       placeholder="Preferred display name">
              </mat-form-field>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <!-- Contact Information Card -->
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>contact_phone</mat-icon>
            Contact Information
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid two-column">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email <span class="required-indicator">*</span></mat-label>
              <input matInput
                     type="email"
                     formControlName="email"
                     placeholder="email@example.com"
                     required
                     [readonly]="isEditMode">
              @if (isEditMode) {
                <mat-icon matSuffix>lock</mat-icon>
              } @else {
                <mat-icon matSuffix>email</mat-icon>
              }
              @if (form.get('email')?.hasError('required')) {
                <mat-error>Email is required</mat-error>
              }
              @if (form.get('email')?.hasError('email')) {
                <mat-error>Please enter a valid email</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Office Phone</mat-label>
              <input matInput
                     formControlName="officePhone"
                     placeholder="(555) 123-4567">
              <mat-icon matSuffix>business</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Mobile Phone</mat-label>
              <input matInput
                     formControlName="mobilePhone"
                     placeholder="(555) 123-4567">
              <mat-icon matSuffix>smartphone</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Fax Phone</mat-label>
              <input matInput
                     formControlName="faxPhone"
                     placeholder="(555) 123-4567">
              <mat-icon matSuffix>print</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- User Permissions Card -->
      <mat-card class="info-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>security</mat-icon>
            Permissions
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="form-grid two-column">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Departments</mat-label>
              <mat-select formControlName="departments" multiple placeholder="Select departments">
                @for (dept of allDepartments; track dept) {
                  <mat-option [value]="dept">{{ dept }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>business_center</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Sites</mat-label>
              <mat-select formControlName="sites"
                          multiple
                          placeholder="Select sites"
                          [compareWith]="compareSites">
                @for (site of allSites; track site.siteNum) {
                  <mat-option [value]="site">{{ site.name }}</mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>location_on</mat-icon>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Additional Details (Collapsible) -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>description</mat-icon>
            Additional Details
          </mat-panel-title>
        </mat-expansion-panel-header>
        <div class="expansion-content">
          <div class="form-grid">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Other Information</mat-label>
              <textarea matInput
                        formControlName="other"
                        rows="3"
                        placeholder="Additional information"></textarea>
            </mat-form-field>

            @if (isEditMode) {
              <mat-form-field appearance="outline">
                <mat-label>Date Added</mat-label>
                <input matInput
                       formControlName="dateAdded"
                       readonly>
                <mat-icon matSuffix>calendar_today</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Login</mat-label>
                <input matInput
                       formControlName="lastLogin"
                       readonly>
                <mat-icon matSuffix>access_time</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Last Login IP</mat-label>
                <input matInput
                       formControlName="lastLoginIp"
                       readonly>
                <mat-icon matSuffix>computer</mat-icon>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Login Count</mat-label>
                <input matInput
                       type="number"
                       formControlName="loginCount"
                       readonly>
                <mat-icon matSuffix>bar_chart</mat-icon>
              </mat-form-field>
            }
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Bottom Action Bar -->
      <div class="bottom-actions">
        <button mat-stroked-button color="accent" (click)="cancelSubmit()" type="button">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button color="primary"
                [disabled]="!isFormValid() || saving"
                type="submit">
          <mat-icon>save</mat-icon>
          {{ isEditMode ? 'Update User' : 'Save User' }}
        </button>
      </div>

    </form>
  </div>
</div>
